import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useShowAdvanced } from "../../hooks/useUiMode";
import { MoreOptionsRow } from "../profile/ProfileParts";
import { useAuth } from "../../contexts/AuthContext";
import { WorkoutContext } from "../../contexts/WorkoutContext";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import DraggableStepper from "../ui/draggable-stepper";
import DetailSheet from "../session/generate/DetailSheet";
import { GOAL_CATEGORIES, TOP_GOALS } from "../../config/goals";
import {
  PERIODISATION_FRAMEWORKS,
  selectFramework,
  formatWeekList,
} from "../../config/periodisation";
import {
  weeklyMix,
  planProgramme,
  describeCoverage,
  coverageAdvice,
  describePlanEdit,
} from "../../config/programmePlanner";
import { useUpdateWaiting } from "../../hooks/useUpdateWaiting";
import {
  weekdaysFor,
  describeWeekdays,
  parseLocalDate,
  programmeStart,
  resolveFirstDay,
  toIsoDate,
  weekdayOf,
} from "../../utils/programmeSchedule";
import { TrainingCalendar, StartOptions, describeStart, describeFirstWeek } from "./WhenPicker";
import { checkForUpdate, requestUpdate } from "../../serviceWorkerRegistration";
import { cn } from "../../lib/utils";
import {
  LogIn,
  ArrowLeft,
  AlertTriangle,
  ChevronRight,
  RefreshCw,
  X,
} from "lucide-react";


// Long Zone 2 day lengths on offer; only those longer than the session limit
// are shown, since a "long" day shorter than the others means nothing.
const LONG_DAY_OPTIONS = [60, 75, 90, 120, 150, 180];

const SPLIT_OPTIONS = [
  { value: "full_body", label: "Full body", hint: "Every session works the whole body" },
  { value: "upper_lower", label: "Upper / lower", hint: "Upper-body and lower-body days take turns" },
  { value: "ppl", label: "Push / pull / legs", hint: "Three day types in rotation" },
  { value: "body_part", label: "Body part", hint: "One or two muscle groups a day" },
];

// A one-line row that opens a sheet, as on the generate screen.
const DetailRow = ({ label, value, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="grid w-full grid-cols-[minmax(0,1fr)_auto_1rem] items-center gap-3 px-3 py-3 text-left hover:bg-muted/40"
  >
    <span className="text-sm font-medium text-white">{label}</span>
    <span className="max-w-[12rem] truncate text-right text-[13px] text-muted-foreground">
      {value}
    </span>
    <ChevronRight className="h-4 w-4 text-muted-foreground/60" />
  </button>
);

const Choice = ({ selected, title, hint, extra, onClick }) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    onClick={onClick}
    className={cn(
      "grid w-full gap-0.5 rounded-xl border p-3 text-left transition-colors",
      selected
        ? "border-primary bg-primary/10"
        : "border-border bg-background hover:border-primary/40",
    )}
  >
    <span className={cn("text-sm font-semibold", selected ? "text-primary" : "text-white")}>
      {title}
    </span>
    {hint && <span className="text-xs leading-snug text-muted-foreground">{hint}</span>}
    {extra && (
      <span className="mt-1 text-[10px] font-medium uppercase tracking-wider text-primary/70">
        {extra}
      </span>
    )}
  </button>
);

const sectionLabel = (text, hint) => (
  <p className="mb-2 flex items-baseline justify-between text-xs font-semibold text-muted-foreground">
    {text}
    {hint && <span className="font-normal text-muted-foreground/70">{hint}</span>}
  </p>
);

// Map an existing programme row to the form's config shape (edit mode).
const programmeToConfig = (p) => ({
  focus: p.focus || "General Fitness",
  duration_weeks: p.duration_weeks ?? 8,
  sessions_per_week: p.days_per_week ?? 3,
  session_minutes: p.session_minutes || 45,
  long_minutes: p.long_minutes || 0,
  // Chosen training days (#92); null = the default spread for How often.
  weekdays: Array.isArray(p.weekdays) && p.weekdays.length ? p.weekdays : null,
  split: p.split || "full_body",
  // Edit shows the programme's actual framework (not "auto").
  framework_mode: p.progression_style || "auto",
  title: p.title || "",
  notes: p.notes || "",
});

// `onSubmit(config)` receives the final config (create OR edit). `programme`
// (optional) puts the form in edit mode, pre-filled from that programme, with
// a warning when a structural change would regenerate the skeleton (#58).
const ProgrammeDesignForm = ({
  onSubmit,
  onImport,
  onCreateProgramme,
  programme,
  onCancel,
}) => {
  const { isAuthenticated } = useAuth();
  const { userProfile, llmSettings } = useContext(WorkoutContext);
  const isEdit = !!programme;
  const submit = onSubmit || onCreateProgramme;
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [config, setConfig] = useState(() =>
    programme
      ? programmeToConfig(programme)
      : {
          focus: "General Fitness",
          duration_weeks: 8,
          sessions_per_week: 3,
          session_minutes:
            userProfile?.sessionDuration || userProfile?.timeAvailable || 45,
          long_minutes: 0,
          weekdays: null,
          split: "full_body",
          // "auto" = derive the framework from goal/schedule/level; any
          // framework value = explicit override from the manual accordion.
          framework_mode: "auto",
          title: "",
          notes: "",
        },
  );

  // #92: make sure the plan is built by the newest rules. Look for a waiting
  // build as the form opens; if there is one, offer it before saving. (The
  // server also refuses a plan from a build that predates the planner.)
  const updateWaiting = useUpdateWaiting();
  useEffect(() => {
    checkForUpdate();
  }, []);

  const touched = useRef(false);
  const handleChange = useCallback((key, value) => {
    touched.current = true;
    setConfig((prev) => ({ ...prev, [key]: value }));
  }, []);

  // The goal's weekly mix of lifting, interval and Zone 2 days for these days
  // a week. The structure is chosen for the lifting days only.
  const mix = useMemo(
    () =>
      weeklyMix({
        focus: config.focus,
        perWeek: config.sessions_per_week,
        userProfile,
      }),
    [config.focus, config.sessions_per_week, userProfile],
  );

  const autoFrameworkValue = useMemo(
    () =>
      selectFramework({
        focus: config.focus,
        sessionsPerWeek: mix.resistance,
        fitnessLevel: userProfile?.fitnessLevel,
      }),
    [config.focus, mix.resistance, userProfile?.fitnessLevel],
  );

  const isAuto = config.framework_mode === "auto";
  const resolvedValue = isAuto ? autoFrameworkValue : config.framework_mode;
  const framework = PERIODISATION_FRAMEWORKS[resolvedValue];

  // The long day only applies when the plan has a Zone 2 day, and only when
  // it is longer than the session limit.
  const longOptions = LONG_DAY_OPTIONS.filter((m) => m > Number(config.session_minutes));
  const longApplies = mix.zone2 > 0 && longOptions.length > 0;
  const longMinutes =
    longApplies && Number(config.long_minutes) > Number(config.session_minutes)
      ? Number(config.long_minutes)
      : 0;

  // The training days: chosen, or the default spread (null above 7 a week).
  const weekdays = weekdaysFor(Number(config.sessions_per_week), config.weekdays);
  const toggleWeekday = (day) => {
    const current = weekdays || [];
    const next = current.includes(day) ? current.filter((d) => d !== day) : [...current, day];
    if (next.length === 0) return; // at least one day
    next.sort((a, b) => a - b);
    touched.current = true;
    setConfig((prev) => ({ ...prev, weekdays: next, sessions_per_week: next.length }));
  };

  // #125: when week 1 starts. A new programme picks today, next Monday or a
  // date (null mode = the suggestion); an edit shows the programme's own.
  const [today] = useState(() => parseLocalDate(new Date()));
  const [start, setStart] = useState({ mode: null });
  const firstDay = isEdit
    ? parseLocalDate(programme.first_day) || programmeStart(programme) || today
    : resolveFirstDay(start, today, weekdays);

  // Edit mode: anything the day plan depends on re-plans the weeks.
  const structureChanged =
    isEdit &&
    (Number(config.duration_weeks) !== Number(programme.duration_weeks) ||
      Number(config.sessions_per_week) !== Number(programme.days_per_week) ||
      config.split !== programme.split ||
      resolvedValue !== programme.progression_style ||
      config.focus !== programme.focus ||
      Number(config.session_minutes) !== Number(programme.session_minutes || 0) ||
      longMinutes !== Number(programme.long_minutes || 0) ||
      String(weekdays || "") !== String(weekdaysFor(programme) || ""));

  // The rules' inputs. Editing a programme without re-planning it keeps the
  // deload weeks the user moved (#94).
  const planInputs = useMemo(
    () => ({
      focus: config.focus,
      durationWeeks: config.duration_weeks,
      perWeek: config.sessions_per_week,
      split: config.split,
      framework: resolvedValue,
      sessionMinutes: config.session_minutes,
      longMinutes,
      weekdays,
      deloadWeeks:
        isEdit && !structureChanged && Array.isArray(programme.deload_weeks)
          ? programme.deload_weeks
          : undefined,
      userProfile,
    }),
    [
      weekdays,
      config.focus,
      config.duration_weeks,
      config.sessions_per_week,
      config.split,
      config.session_minutes,
      resolvedValue,
      longMinutes,
      isEdit,
      structureChanged,
      programme,
      userProfile,
    ],
  );

  // Changes from the notes review (#92): suggested by the LLM, checked and
  // applied by the planner. Only the ones that still pass are kept.
  const [planEdits, setPlanEdits] = useState(() =>
    Array.isArray(programme?.plan_edits) ? programme.plan_edits : [],
  );
  const plan = useMemo(
    () => planProgramme({ ...planInputs, edits: planEdits }),
    [planInputs, planEdits],
  );
  const keptEdits = plan.review.accepted;
  // Week 1's days before the first day aren't scheduled (#125).
  const weekOne = plan.slots.filter(
    (s) => s.week_number === 1 && (!weekdays || weekdays[s.day_index] >= weekdayOf(firstDay)),
  );
  const shortFirstWeek = !!weekdays && weekOne.length < weekdays.length;
  const coverageLine = describeCoverage(plan.coverage);
  const coverageNote = coverageAdvice(plan.coverage);

  const editsChanged =
    isEdit &&
    JSON.stringify(keptEdits) !==
      JSON.stringify(Array.isArray(programme.plan_edits) ? programme.plan_edits : []);
  const planChanged = structureChanged || editsChanged;

  // idle | checking | done | error
  const [review, setReview] = useState({ status: "idle" });
  const reviewAbort = useRef(null);
  useEffect(() => () => reviewAbort.current?.abort(), []);
  const runReview = async () => {
    reviewAbort.current?.abort();
    const abort = new AbortController();
    reviewAbort.current = abort;
    setReview({ status: "checking" });
    try {
      // Loaded on demand: the provider code is large, and only this button needs it.
      const { default: llmApi } = await import("../../apis/llmApi");
      const suggested = await llmApi.reviewProgrammePlan(
        {
          plan: planProgramme(planInputs),
          notes: config.notes,
          focus: config.focus,
          sessionMinutes: Number(config.session_minutes),
          longMinutes,
          weekdays,
        },
        llmSettings,
        abort.signal,
      );
      const checked = planProgramme({ ...planInputs, edits: suggested }).review;
      touched.current = true;
      setPlanEdits(checked.accepted);
      setReview({
        status: "done",
        suggested: suggested.length,
        left: checked.rejected.length,
      });
    } catch (error) {
      if (error?.name === "AbortError") return;
      setReview({ status: "error", message: error?.message || "The check didn't work." });
    }
  };
  const removeEdit = (index) => {
    touched.current = true;
    setPlanEdits(keptEdits.filter((_, i) => i !== index));
  };

  // Completed days a re-plan would drop: the server keeps every one whose
  // week and day still exist (#92). Library edits arrive without weeks, so
  // the count is unknown there (null).
  const droppedCompleted = useMemo(() => {
    if (!planChanged || !Array.isArray(programme?.weeks)) return null;
    const weeks = Number(config.duration_weeks);
    const perWeek = Number(config.sessions_per_week);
    return programme.weeks
      .flatMap((w) =>
        (w.sessions || []).map((s) => ({ ...s, week_number: s.week_number ?? w.week_number })),
      )
      .filter(
        (s) => s.status === "completed" && (s.week_number > weeks || s.day_index >= perWeek),
      ).length;
  }, [planChanged, programme, config.duration_weeks, config.sessions_per_week]);

  const getDefaultTitle = () =>
    `${config.duration_weeks}-Week ${config.focus}`;

  // Which detail sheet is open: goal | structure | split | when | long | notes | name
  const [detail, setDetail] = useState(null);
  const closeDetail = (open) => {
    if (!open) setDetail(null);
  };

  const deloadWeeks = plan.deloads;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      setShowAuthPrompt(true);
      return;
    }
    const { framework_mode, ...rest } = config;
    submit({
      ...rest,
      long_minutes: longMinutes || null,
      // Only an explicit choice is saved; the default spread stays implied.
      weekdays: config.weekdays && weekdays ? weekdays : null,
      progression_style: resolvedValue,
      title: config.title.trim() || getDefaultTitle(),
      notes: config.notes.trim(),
      // The user's own calendar date, not the server's (#125).
      ...(!isEdit ? { first_day: toIsoDate(firstDay) } : {}),
      // The day plan goes with a new programme and with any edit that
      // re-plans it; a rename or a note leaves the days alone.
      ...(!isEdit || planChanged ? { slots: plan.slots, plan_edits: keptEdits } : {}),
    });
  };

  const isTopGoal = TOP_GOALS.some(([g]) => g === config.focus);
  const splitLabel = SPLIT_OPTIONS.find((o) => o.value === config.split)?.label;
  // Basic mode (#172): structure, split and the long day are tuning most
  // people leave alone. They show when set away from the default, or under
  // More options.
  const { show: showAll, reveal } = useShowAdvanced();
  const showStructure = showAll || !isAuto;
  const showSplit = showAll || config.split !== "full_body";
  const showLong = showAll || !!longMinutes;
  const shortLabel = (fw) => (fw?.label || "").replace(/\s*\(.*\)\s*$/, "");
  const structureValue = isAuto
    ? `Auto · ${shortLabel(framework)}`
    : shortLabel(framework);

  return (
    <div className="mx-auto max-w-5xl pt-4 md:pt-8">
      <form onSubmit={handleSubmit} className="grid gap-6">
        <div>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="mb-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back
            </button>
          )}
          <h2 className="text-[22px] font-bold text-white">
            {isEdit ? "Edit programme" : "New programme"}
          </h2>
          <p className="mt-1 max-w-[52ch] text-[13px] leading-relaxed text-muted-foreground">
            {isEdit
              ? "Changing the goal, how long, how often, the time or the structure re-plans the weeks."
              : "Pick a goal and how often you can train. Khronos plans the weeks, then builds each session as you reach it, learning from the one before."}
          </p>
          {!isEdit && onImport && (
            <button
              type="button"
              onClick={onImport}
              className="mt-2 text-[13px] font-semibold text-primary hover:underline"
            >
              Already have a programme? Import it
            </button>
          )}
        </div>

        <div className="grid grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-2 lg:items-start lg:gap-x-8">
          <div className="grid min-w-0 gap-6">
            <div>
              {sectionLabel("Goal")}
              <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Goal">
                {TOP_GOALS.map(([goal, hint]) => {
                  const selected = config.focus === goal;
                  return (
                    <button
                      key={goal}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => handleChange("focus", goal)}
                      className={cn(
                        "grid gap-0.5 rounded-xl border px-3 py-2.5 text-left transition-colors",
                        selected
                          ? "border-primary bg-primary/10"
                          : "border-border bg-card hover:border-primary/40",
                      )}
                    >
                      <span className={cn("text-sm font-semibold", selected ? "text-primary" : "text-white")}>
                        {goal}
                      </span>
                      <span className="text-xs leading-snug text-muted-foreground">{hint}</span>
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => setDetail("goal")}
                  className={cn(
                    "grid gap-0.5 rounded-xl border border-dashed px-3 py-2.5 text-left transition-colors",
                    isTopGoal
                      ? "border-border hover:border-primary/40"
                      : "border-primary bg-primary/10",
                  )}
                >
                  <span className={cn("text-sm font-semibold", isTopGoal ? "text-muted-foreground" : "text-primary")}>
                    {isTopGoal ? "Other goals" : config.focus}
                  </span>
                  <span className="text-xs leading-snug text-muted-foreground">
                    {isTopGoal ? "Endurance, skills, longevity…" : "Tap to change"}
                  </span>
                </button>
              </div>
            </div>

            <div>
              {sectionLabel("Schedule", "drag or tap")}
              <div className="grid gap-4 rounded-2xl border border-border bg-card p-3">
                <div className="grid grid-cols-2 gap-3">
                  <DraggableStepper
                    label="How long"
                    value={config.duration_weeks}
                    onChange={(v) => handleChange("duration_weeks", v)}
                    min={2}
                    max={16}
                    step={1}
                    unit="weeks"
                  />
                  <DraggableStepper
                    label="How often"
                    value={config.sessions_per_week}
                    onChange={(v) => {
                      touched.current = true;
                      setConfig((prev) => ({ ...prev, sessions_per_week: v, weekdays: null }));
                    }}
                    min={1}
                    max={14}
                    step={1}
                    unit="a week"
                  />
                </div>
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">
                    Up to{" "}
                    <span className="font-normal text-muted-foreground/70">
                      per session, so each day is planned at or under this
                    </span>
                  </p>
                  <DraggableStepper
                    prominent
                    momentum
                    unit="min"
                    ariaLabel="Time available"
                    id="prog-minutes"
                    value={config.session_minutes}
                    onChange={(v) => handleChange("session_minutes", v)}
                    min={15}
                    max={180}
                    step={5}
                  />
                </div>
                {(!isEdit || weekdays) && (
                  <button
                    type="button"
                    onClick={() => setDetail("when")}
                    className="grid w-full grid-cols-[minmax(0,1fr)_1rem] items-center gap-3 rounded-xl border border-border bg-background px-3 py-2.5 text-left hover:border-primary/40"
                  >
                    <span className="min-w-0">
                      <span className="block text-xs font-medium text-muted-foreground">When</span>
                      <span className="mt-0.5 block text-sm font-semibold text-white">
                        {describeStart(firstDay, today)}
                      </span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        {weekdays ? `${describeWeekdays(weekdays)} · ` : `${config.sessions_per_week} a week · `}
                        {describeFirstWeek(firstDay, weekdays, Number(config.sessions_per_week)).toLowerCase()}
                      </span>
                    </span>
                    <ChevronRight className="h-4 w-4 text-muted-foreground/60" />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="grid min-w-0 gap-6">
            <div>
              {sectionLabel("Your programme", structureValue)}
              <div
                className="grid gap-1"
                style={{ gridTemplateColumns: `repeat(${config.duration_weeks}, minmax(0, 1fr))` }}
                aria-label={`${config.duration_weeks} weeks`}
              >
                {Array.from({ length: config.duration_weeks }, (_, i) => {
                  const deload = deloadWeeks.includes(i + 1);
                  return (
                    <span
                      key={i}
                      className={cn(
                        "grid h-6 place-items-center rounded-md text-[10px] tabular-nums",
                        deload
                          ? "bg-yellow-500/15 font-bold text-yellow-500"
                          : "bg-white/[0.07] text-muted-foreground",
                      )}
                    >
                      {deload ? "D" : i + 1}
                    </span>
                  );
                })}
              </div>
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                {deloadWeeks.length > 0
                  ? `${deloadWeeks.length > 1 ? "Weeks" : "Week"} ${formatWeekList(deloadWeeks)} ${deloadWeeks.length > 1 ? "are lighter deload weeks" : "is a lighter deload week"}, so you recover and absorb the training.`
                  : "Programmes of 6 weeks or more end on a lighter deload week."}
              </p>
              <p className="mb-1.5 mt-3 text-xs font-semibold text-muted-foreground">
                Week 1{shortFirstWeek && <span className="font-normal text-muted-foreground/70"> · short, from {describeWeekdays([weekdayOf(firstDay)])}</span>}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {weekOne.slice(0, 7).map((slot) => (
                  <span
                    key={slot.day_index}
                    className="rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs text-foreground"
                  >
                    {weekdays && (
                      <span className="mr-1.5 font-semibold text-muted-foreground">
                        {describeWeekdays([weekdays[slot.day_index]])}
                      </span>
                    )}
                    {slot.scheduled_label}
                    <span className="ml-1.5 tabular-nums text-muted-foreground">
                      {slot.target_minutes} min
                    </span>
                  </span>
                ))}
                {weekOne.length > 7 && (
                  <span className="px-2 py-1.5 text-xs text-muted-foreground">
                    +{weekOne.length - 7} more
                  </span>
                )}
              </div>
              {coverageLine && (
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                  <span className="text-foreground">{coverageLine}</span>
                  {coverageNote && ` ${coverageNote}`}
                </p>
              )}
              {(config.notes.trim() || keptEdits.length > 0) && (
                <div className="mt-3 border-t border-border pt-3">
                  {keptEdits.length > 0 && (
                    <>
                      <p className="mb-1.5 text-xs font-semibold text-muted-foreground">
                        Changes from your notes
                      </p>
                      <ul className="mb-2 grid gap-1.5">
                        {keptEdits.map((edit, i) => (
                          <li
                            key={i}
                            className="flex items-start gap-2 rounded-lg border border-border bg-card px-2.5 py-2 text-xs"
                          >
                            <span className="min-w-0 flex-1">
                              <span className="block font-medium text-foreground">
                                {describePlanEdit(edit, weekdays)}
                              </span>
                              <span className="block text-muted-foreground">{edit.reason}</span>
                            </span>
                            <button
                              type="button"
                              onClick={() => removeEdit(i)}
                              aria-label="Remove this change"
                              className="-m-1 rounded-md p-1 text-muted-foreground hover:text-foreground"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  {config.notes.trim() && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={review.status === "checking"}
                      onClick={runReview}
                    >
                      {review.status === "checking"
                        ? "Checking\u2026"
                        : keptEdits.length > 0
                          ? "Check again"
                          : "Check the plan against my notes"}
                    </Button>
                  )}
                  {review.status === "done" && (review.suggested === 0 || review.left > 0) && (
                    <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                      {review.suggested === 0
                        ? "Your notes don't need any changes to the plan."
                        : `Left out ${review.left} ${review.left === 1 ? "change" : "changes"} that would break your weekly targets or time limits.`}
                    </p>
                  )}
                  {review.status === "error" && (
                    <p className="mt-2 text-xs leading-relaxed text-destructive">{review.message}</p>
                  )}
                </div>
              )}
            </div>

            <div>
              {sectionLabel("Details")}
              <div className="overflow-hidden rounded-2xl border border-border bg-card divide-y divide-border">
                {showStructure && (
                  <DetailRow label="Structure" value={structureValue} onClick={() => setDetail("structure")} />
                )}
                {showSplit && <DetailRow label="Split" value={splitLabel} onClick={() => setDetail("split")} />}
                {longApplies && showLong && (
                  <DetailRow
                    label="Long day"
                    value={longMinutes ? `${longMinutes} min Zone 2` : "Off"}
                    onClick={() => setDetail("long")}
                  />
                )}
                <DetailRow
                  label="Goals and limits"
                  value={config.notes.trim() || "None"}
                  onClick={() => setDetail("notes")}
                />
                <DetailRow
                  label="Name"
                  value={config.title.trim() || getDefaultTitle()}
                  onClick={() => setDetail("name")}
                />
              </div>
              {!showAll && (
                <div className="mt-1">
                  <MoreOptionsRow onClick={reveal} />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="sticky bottom-14 z-10 -mx-4 grid gap-3 border-t border-border bg-background/95 px-4 py-3 backdrop-blur md:bottom-0 lg:mx-0 lg:justify-items-end lg:border-0 lg:bg-transparent lg:px-0 lg:backdrop-blur-none">
          {updateWaiting && (
            <div className="flex w-full items-start gap-3 rounded-lg border border-primary/30 bg-primary/10 p-3 text-sm">
              <RefreshCw className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div className="grid gap-2">
                <p className="text-xs leading-relaxed text-muted-foreground">
                  A newer version of Khronos is ready. Update before saving so
                  your plan is built with the latest rules
                  {touched.current ? "; you'll need to make these changes again" : ""}.
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="w-fit"
                  onClick={() => requestUpdate()}
                >
                  Update now
                </Button>
              </div>
            </div>
          )}
          {isEdit && planChanged && (
            <div className="flex w-full items-start gap-3 rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-3 text-sm">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-yellow-500" />
              <p className="text-xs leading-relaxed text-muted-foreground">
                You changed something the plan depends on, so saving re-plans
                the weeks.{" "}
                {droppedCompleted > 0 ? (
                  <span className="font-medium text-foreground">
                    {droppedCompleted} completed{" "}
                    {droppedCompleted === 1 ? "day falls" : "days fall"} outside the
                    new plan and will be removed from it.
                  </span>
                ) : droppedCompleted === 0 ? (
                  "Completed days are kept."
                ) : (
                  "Completed days that still fit are kept; any outside the new plan are removed from it."
                )}{" "}
                Sessions built for days that change are cleared. Your workout
                history is kept.
              </p>
            </div>
          )}
          {showAuthPrompt && !isAuthenticated && (
            <div className="flex w-full items-start gap-3 rounded-lg border border-primary/20 bg-primary/5 p-3 text-sm">
              <LogIn className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <p className="text-muted-foreground">
                Sign in to create and save your programme.
              </p>
            </div>
          )}
          <Button type="submit" className="h-12 w-full text-base font-bold lg:w-72">
            {isEdit ? "Save changes" : "Create programme"}
          </Button>
        </div>
      </form>

      <DetailSheet
        open={detail === "goal"}
        onOpenChange={closeDetail}
        title="Goal"
        description="What this programme is for"
        showDescription
      >
        {Object.entries(GOAL_CATEGORIES).map(([category, goals]) => (
          <div key={category} className="space-y-2">
            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              {category}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {goals.map((goal) => (
                <button
                  key={goal}
                  type="button"
                  aria-pressed={config.focus === goal}
                  onClick={() => handleChange("focus", goal)}
                  className={cn(
                    "rounded-full border px-3 py-2 text-[13px] transition-colors",
                    config.focus === goal
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border bg-background text-foreground hover:border-primary/40",
                  )}
                >
                  {goal}
                </button>
              ))}
            </div>
          </div>
        ))}
      </DetailSheet>

      <DetailSheet
        open={detail === "structure"}
        onOpenChange={closeDetail}
        title="Structure"
        description="How the weeks progress. All of them work, so pick the one you'll stick with."
        showDescription
      >
        <div className="grid gap-2" role="radiogroup" aria-label="Structure">
          <Choice
            selected={isAuto}
            title="Auto (recommended)"
            hint={`Picked from your goal, schedule and experience. Right now: ${shortLabel(PERIODISATION_FRAMEWORKS[autoFrameworkValue])}.`}
            onClick={() => handleChange("framework_mode", "auto")}
          />
          {Object.values(PERIODISATION_FRAMEWORKS).map((fw) => (
            <Choice
              key={fw.value}
              selected={!isAuto && config.framework_mode === fw.value}
              title={shortLabel(fw)}
              hint={fw.description}
              extra={fw.evidence}
              onClick={() => handleChange("framework_mode", fw.value)}
            />
          ))}
        </div>
      </DetailSheet>

      <DetailSheet
        open={detail === "split"}
        onOpenChange={closeDetail}
        title="Split"
        description="How the body is divided across the week"
        showDescription
      >
        <div className="grid gap-2" role="radiogroup" aria-label="Split">
          {SPLIT_OPTIONS.map((opt) => (
            <Choice
              key={opt.value}
              selected={config.split === opt.value}
              title={opt.label}
              hint={opt.hint}
              onClick={() => handleChange("split", opt.value)}
            />
          ))}
        </div>
      </DetailSheet>

      <DetailSheet
        open={detail === "when"}
        onOpenChange={closeDetail}
        title="When you train"
        description={
          isEdit
            ? "Khronos puts the hardest days where there's rest around them"
            : "Your training days, and the day you start"
        }
        showDescription
      >
        <TrainingCalendar
          days={weekdays}
          perWeek={Number(config.sessions_per_week)}
          firstDay={firstDay}
          today={today}
          onToggleDay={weekdays ? toggleWeekday : undefined}
        />
        {!isEdit && (
          <StartOptions
            choice={start}
            onChange={(next) => {
              touched.current = true;
              setStart(next);
            }}
            firstDay={firstDay}
            today={today}
            days={weekdays}
            perWeek={Number(config.sessions_per_week)}
          />
        )}
        {weekdays && (
          <p className="text-xs text-muted-foreground">
            Changing How often goes back to an even spread.
          </p>
        )}
      </DetailSheet>

      <DetailSheet
        open={detail === "long"}
        onOpenChange={closeDetail}
        title="Long day"
        description="One longer Zone 2 session a week, on top of your usual time"
        showDescription
      >
        <div className="grid gap-2" role="radiogroup" aria-label="Long day">
          <Choice
            selected={!longMinutes}
            title="Off"
            hint="Every Zone 2 day fits your usual session time"
            onClick={() => handleChange("long_minutes", 0)}
          />
          {longOptions.map((m) => (
            <Choice
              key={m}
              selected={longMinutes === m}
              title={`${m} min`}
              hint="Goes on the week's last Zone 2 day; you can move it"
              onClick={() => handleChange("long_minutes", m)}
            />
          ))}
        </div>
      </DetailSheet>

      <DetailSheet
        open={detail === "notes"}
        onOpenChange={closeDetail}
        title="Goals and limits"
        description="Used every time a session in this programme is built"
        showDescription
      >
        <Textarea
          id="prog-notes"
          value={config.notes}
          onChange={(e) => handleChange("notes", e.target.value)}
          placeholder="e.g. Hiking trip in spring, protect my left knee, prefer barbell work"
          rows={4}
          className="resize-none bg-background text-base"
        />
      </DetailSheet>

      <DetailSheet
        open={detail === "name"}
        onOpenChange={closeDetail}
        title="Name"
        description="Leave blank to use the suggested name"
        showDescription
      >
        <Input
          id="prog-title"
          value={config.title}
          onChange={(e) => handleChange("title", e.target.value)}
          placeholder={getDefaultTitle()}
          className="bg-background text-base"
        />
      </DetailSheet>
    </div>
  );
};

export default ProgrammeDesignForm;
